import React, { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { TypedMemo } from '../../common/types';
import { useSMLRouter } from '../../../routing/sml/hooks';
import { useUserTokenDecoded } from '../../../redux/slices/appInfo/hooks';
import { SMLRoutes } from '../../../routing/sml/SMLRoutes';
import { useChecklistState } from './useChecklistState';
import { useChecklistAnalytics } from './useChecklistAnalytics';
import { useChecklistVisibility } from './useChecklistVisibility';
import { ChecklistProgress } from './ChecklistProgress';
import { scrollToFirstReleaseChecklist } from './scrollToFirstReleaseChecklist';
import { noteChecklistOpen, showsChecklistDoneRow } from './checklistDoneRow';
import { checklistItemMessages, checklistMessages as messages } from './checklistMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the checklist; each part uses its own slice of it */
import styles from './FirstReleaseChecklist.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SidebarChecklistPillProps {
  /** The collapsed rail: the ring alone, with a tooltip. */
  isRail?: boolean;
}

/**
 * The checklist row above the account card (design navigation-v2 1a, 6d): the ring, "First
 * release" and "8/9". A click opens Home at the checklist (6c). On hover a × hides the checklist,
 * the same as "Dismiss" on Home. It shows the checklist of the active artist. When the last step is
 * done, the row shows a green check for 24 hours (6d "Done", `checklistDoneRow.ts`), then nothing. It
 * renders nothing when the checklist is dismissed, or for a member (onboarding d7).
 */
const SidebarChecklistPillComponent: React.FC<SidebarChecklistPillProps> = ({ isRail = false }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const { state, accountId } = useChecklistState();
  const { dismiss } = useChecklistVisibility(accountId);
  const checklist = state?.checklist ?? null;
  useChecklistAnalytics(state);
  const openChecklist = useCallback(() => {
    router.push(SMLRoutes.home);
    scrollToFirstReleaseChecklist();
  }, [router]);
  const userId = useUserTokenDecoded()?.id ?? null;
  const isComplete = checklist?.complete;
  const [showsDone, setShowsDone] = useState(false);
  useEffect(() => {
    if (userId === null || accountId === null || isComplete === undefined) {
      setShowsDone(false);
      return;
    }
    if (isComplete) {
      setShowsDone(showsChecklistDoneRow(userId, accountId, Date.now()));
    } else {
      noteChecklistOpen(userId, accountId);
      setShowsDone(false);
    }
  }, [userId, accountId, isComplete]);

  if (checklist === null) {
    return null;
  }
  if (checklist.complete) {
    if (!showsDone) {
      return null;
    }
    const doneFigure = intl.formatMessage(messages.progress, { done: checklist.total, total: checklist.total });
    const doneTitle = intl.formatMessage(messages.rowDone);
    return (
      <div className={clsx(styles.row, styles.rowDone, isRail && styles.rowRail)}>
        <button
          type="button"
          className={styles.rowOpen}
          onClick={openChecklist}
          title={doneTitle}
          data-testid="sidebar-checklist-done"
        >
          <span className={styles.doneCheck} role="img" aria-label={doneTitle} />
          <b className={styles.rowTitle}>{doneTitle}</b>
          <span className={styles.rowFigure}>{doneFigure}</span>
        </button>
      </div>
    );
  }

  const nextItem = checklist.items.find((item) => !item.done);
  const title = intl.formatMessage(messages.row);
  const figure = intl.formatMessage(messages.progress, { done: checklist.doneCount, total: checklist.total });
  const line =
    nextItem === undefined
      ? figure
      : intl.formatMessage(messages.pillNext, {
          done: checklist.doneCount,
          total: checklist.total,
          item: intl.formatMessage(checklistItemMessages[nextItem.key].title),
        });

  return (
    <div className={clsx(styles.row, isRail && styles.rowRail)}>
      <button
        type="button"
        className={styles.rowOpen}
        onClick={openChecklist}
        title={`${title} · ${line}`}
        data-testid="sidebar-checklist-pill"
      >
        <ChecklistProgress done={checklist.doneCount} total={checklist.total} small />
        <b className={styles.rowTitle}>{title}</b>
        <span className={styles.rowFigure}>{figure}</span>
      </button>
      {!isRail && (
        <button
          type="button"
          className={styles.rowHide}
          onClick={dismiss}
          aria-label={intl.formatMessage(messages.rowHide)}
          title={intl.formatMessage(messages.rowHide)}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      )}
    </div>
  );
};

export const SidebarChecklistPill = TypedMemo(SidebarChecklistPillComponent);
